import { useEffect, useState } from "react";

import { track } from "../analytics";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import { Band } from "../components/Ornaments";
import { ScrollReveal } from "../components/ScrollReveal";
import { useSmoothScroll } from "../hooks/useSmoothScroll";

import {
  getEventPhase,
  splitCommunityEvents,
  type VyEvent,
} from "./eventsConfig";

/**
 * Events index for the NZ chapter — community sessions at venues
 * across Aotearoa, each with a dedicated /events/<slug> registration
 * page (flyer QR deep-links).
 *
 * Past community sessions collapse into a "Past sessions" archive
 * below the upcoming list — visitors can still find a session they
 * went to, but the page never leads with a stale event.
 *
 * Flyer QR codes may deep-link via /events#<slug>. On mount we read
 * the hash, scroll the matching card into view, and apply a temporary
 * focus border. If the deep link points at a past event, we
 * auto-expand the archive so the focused card is actually visible.
 */
export function EventsIndex() {
  useSmoothScroll();

  const [focusedSlug, setFocusedSlug] = useState<string | null>(null);
  const [pastOpen, setPastOpen] = useState(false);

  useEffect(() => {
    track("events_index_viewed");
  }, []);

  const { upcoming, past } = splitCommunityEvents();

  // Resolve `#<slug>` deep links: scroll the card into view and mark
  // it focused so a temporary highlight ring appears.
  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      if (!hash) {
        return;
      }
      // If the deep-link target is a past session, open the archive
      // first so the element is rendered when we go to scroll to it.
      if (past.some((e) => e.slug === hash)) {
        setPastOpen(true);
      }
      // Allow ScrollReveal motion-divs to mount before scrolling, so
      // the target element actually has a non-zero offset.
      requestAnimationFrame(() => {
        const el = document.getElementById(`event-${hash}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setFocusedSlug(hash);
          track("event_detail_viewed", { slug: hash });
        }
      });
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [past]);

  return (
    <>
      <Nav />
      <main className="bg-cream">
        {/* Page header */}
        <section className="px-6 pt-32 pb-12 md:pt-40 md:pb-16">
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <p className="eyebrow">Where you&rsquo;ll find us</p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1
                className="font-display mt-5 text-warm-brown"
                style={{
                  fontSize: "clamp(2.6rem, 6.2vw, 4.8rem)",
                  lineHeight: 1.04,
                  letterSpacing: "0.004em",
                }}
              >
                Come meditate with us,{" "}
                <span className="text-gold">across Aotearoa.</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="mt-7 max-w-2xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
                A short list of places and times we're meeting — community
                sessions at venues across Aotearoa, free and open to anyone.
              </p>
            </ScrollReveal>
            <Band className="mt-12 justify-start" />
          </div>
        </section>

        {/* Community sessions */}
        {upcoming.length > 0 || past.length > 0 ? (
          <section className="px-6 pb-24 md:pb-32">
            <div className="mx-auto max-w-3xl">
              <ScrollReveal>
                <SectionEyebrow index="01">Community sessions</SectionEyebrow>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <p className="mt-4 mb-7 text-[15px] leading-relaxed text-warm-brown-light md:text-base">
                  Free, in-person, with a short walkthrough of the technique.
                  Tap "Register" to reserve your seat — we'll email a ticket for
                  the door.
                </p>
              </ScrollReveal>

              {upcoming.length > 0 ? (
                <ul className="space-y-5">
                  {upcoming.map((event, idx) => (
                    <ScrollReveal key={event.slug} delay={0.15 + idx * 0.05}>
                      <CommunityCard
                        event={event}
                        isFocused={focusedSlug === event.slug}
                      />
                    </ScrollReveal>
                  ))}
                </ul>
              ) : (
                <p className="border border-(--color-hairline) bg-warm-white px-6 py-8 text-center text-[15px] text-warm-brown-light md:text-base">
                  No upcoming community sessions on the books just yet. The
                  weekly Sunday Meetup runs every week — join us there.
                </p>
              )}

              {past.length > 0 ? (
                <details
                  className="group hairline-t hairline-b mt-10 px-1 py-5 md:py-6"
                  open={pastOpen}
                  onToggle={(e) => {
                    const open = e.currentTarget.open;
                    setPastOpen(open);
                    if (open) {
                      track("events_past_expanded");
                    }
                  }}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                    <div>
                      <p className="eyebrow">Past sessions</p>
                      <p className="mt-1 text-sm text-warm-brown-light">
                        {past.length} previous{" "}
                        {past.length === 1 ? "session" : "sessions"}
                      </p>
                    </div>
                    <span className="text-sm text-warm-brown-light transition-transform duration-300 group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <ul className="mt-6 space-y-4">
                    {past.map((event) => (
                      <PastCommunityRow
                        key={event.slug}
                        event={event}
                        isFocused={focusedSlug === event.slug}
                      />
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
    </>
  );
}

function SectionEyebrow({
  index,
  children,
}: {
  index: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="verse-index">{index}</span>
      <p className="eyebrow">{children}</p>
      <span className="hairline h-px flex-1 self-center" aria-hidden />
    </div>
  );
}

/**
 * Visual treatment for a hash-focused card. A subtle gold ring +
 * stronger shadow so the visitor's eye lands on the right item
 * without the card looking like a clickbait alert.
 */
function focusRingClasses(isFocused: boolean): string {
  return isFocused
    ? "ring-2 ring-gold/60 shadow-lg shadow-warm-brown/12 border-gold/35"
    : "";
}

interface CardProps {
  event: VyEvent;
  isFocused: boolean;
}

function CommunityCard({ event, isFocused }: CardProps) {
  const venue = event.venue;
  return (
    <li
      id={`event-${event.slug}`}
      className={`border border-(--color-hairline) bg-warm-white p-6 transition-all duration-500 md:p-8 ${focusRingClasses(isFocused)}`}
    >
      <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-6">
        <p className="text-[15px] font-semibold text-warm-brown md:text-base">
          {event.dateLabel}
        </p>
        <p className="text-sm text-warm-brown-light">{event.timeLabel}</p>
      </div>

      {venue ? (
        <div className="mt-4">
          <p className="text-base font-semibold text-warm-brown md:text-lg">
            {venue.name}
            {venue.hall ? (
              <>
                {" · "}
                <span className="text-warm-brown-light">{venue.hall}</span>
              </>
            ) : null}
          </p>
          <p className="mt-0.5 text-sm text-warm-brown-light">{venue.suburb}</p>
        </div>
      ) : null}

      {event.longCopy ? (
        <p className="mt-5 text-sm leading-relaxed text-warm-brown-light md:text-[15px]">
          {event.longCopy}
        </p>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-2.5">
        <a
          href={`/events/${event.slug}`}
          className="inline-flex items-center gap-2 rounded-full bg-warm-brown px-5 py-2.5 text-[0.8rem] font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-gold"
        >
          {getEventPhase(event) === "active" ? "Check in" : "Register"}
          <span className="text-cream/70">→</span>
        </a>
        {event.startsAt ? (
          <a
            href={buildIcsHref(event)}
            download={`${event.slug}.ics`}
            className="inline-flex items-center gap-2 rounded-full border border-(--color-hairline-strong) px-5 py-2.5 text-[0.8rem] font-medium tracking-wide text-warm-brown transition-colors duration-300 hover:border-warm-brown"
          >
            Add to calendar
            <span className="text-warm-brown/60">↓</span>
          </a>
        ) : null}
        {venue ? (
          <a
            href={venue.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-(--color-hairline-strong) px-5 py-2.5 text-[0.8rem] font-medium tracking-wide text-warm-brown transition-colors duration-300 hover:border-warm-brown"
          >
            Get directions
            <span className="text-warm-brown/60">↗</span>
          </a>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Compact past-session row. No registration form, no calendar — the
 * event has happened. Keeps map + Facebook references so a visitor
 * can still find where it was.
 */
function PastCommunityRow({ event, isFocused }: CardProps) {
  const venue = event.venue;
  return (
    <li
      id={`event-${event.slug}`}
      className={`hairline-b px-1 py-4 transition-all duration-500 last:border-b-0 md:py-5 ${focusRingClasses(isFocused)}`}
    >
      <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-4">
        <p className="text-sm font-medium text-warm-brown">{event.dateLabel}</p>
        <p className="text-xs text-warm-brown-light md:text-sm">
          {event.timeLabel}
        </p>
      </div>
      {venue ? (
        <p className="mt-2 text-sm text-warm-brown-light">
          {venue.name}
          {venue.hall ? <> · {venue.hall}</> : null}
        </p>
      ) : null}
    </li>
  );
}

/**
 * Builds a `data:text/calendar` href with a minimal valid VEVENT.
 * Clicking the `<a download>` saves the .ics file; the visitor's OS
 * opens it with their default calendar app. End time = endsAt if
 * set, otherwise startsAt + 60 min (the community sessions default
 * to one-hour slots).
 */
function buildIcsHref(event: VyEvent): string {
  const start = event.startsAt;
  if (!start) {
    return "#";
  }
  const startDate = new Date(start);
  const endDate = event.endsAt
    ? new Date(event.endsAt)
    : new Date(startDate.getTime() + 60 * 60 * 1000);
  const fmt = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const escape = (s: string) =>
    s
      .replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;");
  const locationParts = event.venue
    ? [event.venue.name, event.venue.hall, event.venue.suburb]
        .filter(Boolean)
        .join(", ")
    : "";
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Vihangam Yog New Zealand//Events//EN",
    "BEGIN:VEVENT",
    `UID:${event.slug}@vyaustralia.org`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(startDate)}`,
    `DTEND:${fmt(endDate)}`,
    `SUMMARY:${escape(event.title)}`,
    `DESCRIPTION:${escape(event.longCopy ?? event.blurb)}`,
    locationParts ? `LOCATION:${escape(locationParts)}` : "",
    `URL:https://vihangamyognz.org/events#${event.slug}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);
  const ics = lines.join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}
